import { Flex } from "@portal/BaseComponents/Flex/Flex";
import { Col, Row } from "@portal/BaseComponents/Grid/AntGrid";
import { HideWrapper } from "@portal/BaseComponents/HideWrapper/HideWrapper";
import { Profile } from "@portal/BaseComponents/Profile/Profile";
import { TypographyText } from "@portal/BaseComponents/Typography/AntTypography";
import { Style } from "@portal/lib/style/style";
import { userConfigAtom } from "@portal/store/userConfig/userConfig";
import { Customer_Contacts } from "@portal/types/entities/customer";
import { Empty } from "antd";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { TitleComponent } from "../Title";
import { ProfileCards } from "../style";

const t = {
  TICycleContacts: "TI Cycles Contacts", //This title should be dynamic,change later
  CollectionContact: "Collection Contact",
  OtherContacts: "Other Contacts",
};

export interface contactItemProps extends Customer_Contacts {
  hasChild?: boolean;
  className?: string;
}

interface ContactsContainerProps {
  contact: Array<Customer_Contacts>;
  title: string;
  hasChild?: boolean;
  className?: string;
}

const StyledContactItem = Style(Row, {
  width: "100%",
  paddingBlock: "12px",

  variants: {
    border: {
      true: {
        borderBottom: "1px solid var(--gray-5)",

        "&.other-contacts": {
          "&:last-child": {
            borderBottom: 0,
          },
        },
      },
    },
  },
});

function ContactItem(props: contactItemProps) {
  const {
    first_name,
    last_name,
    designation,
    email,
    phone,
    hasChild,
    className,
  } = props;

  return (
    <StyledContactItem wrap={false} border={hasChild} className={className}>
      <Col flex="250px">
        <Profile
          firstName={first_name}
          lastName={last_name}
          designation={designation}
          avatarStyle={{ size: "default" }}
        />
      </Col>
      <Col flex="auto">
        <Flex vertical space={"2"}>
          <TypographyText fs="12">{email}</TypographyText>
          <TypographyText fs="12">{phone}</TypographyText>
        </Flex>
      </Col>
    </StyledContactItem>
  );
}

function ContactsContainer(props: ContactsContainerProps) {
  const { contact, title, hasChild, className } = props;
  const Title = (
    <TypographyText fs="12" strong>
      {title}
    </TypographyText>
  );
  return (
    <Flex vertical style={{ width: "100%" }}>
      {Title}
      {contact.map((contacts) => {
        return (
          <ContactItem
            {...contacts}
            key={contacts.id}
            hasChild={hasChild}
            className={className}
          />
        );
      })}
    </Flex>
  );
}

function InternalContacts() {
  const config = useAtomValue(userConfigAtom);
  function getOtherContacts() {
    const contacts: Array<Customer_Contacts> = [];

    if (config.user_details?.customer?.account_manager) {
      contacts.push({
        ...config.user_details.customer.account_manager,
        designation: "Account Manager",
      });
    }
    if (config.user_details?.customer?.cs_manager) {
      contacts.push({
        ...config.user_details.customer.cs_manager,
        designation: "Customer Success Manager",
      });
    }

    return contacts;
  }

  function getCollectionContacts() {
    return config?.user_details?.customer.owner
      ? [
          {
            ...config.user_details.customer.owner,
            designation: "Collection Owner",
          },
        ]
      : [];
  }
  const otherContacts = useMemo(getOtherContacts, [
    config?.user_details?.customer.account_manager,
    config?.user_details?.customer.cs_manager,
  ]);
  const collectionsContacts = useMemo(getCollectionContacts, [
    config.user_details?.customer.owner,
  ]);
  const Title = (
    <TitleComponent
      title={`${config.visualConfig.config?.company_name}'s Contacts`}
      internalContactsLength={otherContacts.length + collectionsContacts.length}
    />
  );
  const EmptyComp = (
    <Empty
      description={`${config.user_details?.customer.name}'s contacts available`}
      image={Empty.PRESENTED_IMAGE_SIMPLE}
    />
  );
  return (
    <ProfileCards style={{ minHeight: "100%" }} title={Title}>
      {Boolean(collectionsContacts.length) || Boolean(otherContacts.length) ? (
        <Flex vertical style={{ width: "100%" }} space={"16"}>
          {config.user_details?.customer ? (
            <ContactsContainer
              contact={collectionsContacts}
              title={t.CollectionContact}
              hasChild={Boolean(otherContacts.length)}
            />
          ) : null}
          {config.user_details?.customer ? (
            <HideWrapper hide={!otherContacts.length}>
              <ContactsContainer
                contact={otherContacts}
                title={t.OtherContacts}
                hasChild={otherContacts.length > 1}
                className="other-contacts"
              />
            </HideWrapper>
          ) : null}
        </Flex>
      ) : (
        EmptyComp
      )}
    </ProfileCards>
  );
}
export { InternalContacts };
